import React, { useState, useCallback, useEffect } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import { FiPlus, FiTrash2, FiEdit2, FiUploadCloud, FiImage } from "react-icons/fi";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl, imgurl } from "../baseurl"; // ensure correct URLs

function InterfaceControl() {
  const [banners, setBanners] = useState([]);
  const [newBanner, setNewBanner] = useState({
    title: "",
    link: "",
    status: "active",
    image: "",
    imageFile: null,
  });
  const [editBannerUuid, setEditBannerUuid] = useState(null);
  const [loading, setLoading] = useState(false);

  // SweetAlert2 Toast setup
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
  });

  // 🟢 Fetch all banners
  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${baseurl}getAllBanners`);
      setBanners(res.data.banners || []);
    } catch (err) {
      console.error("Error fetching banners:", err);
      Toast.fire({ icon: "error", title: "Failed to fetch banners" });
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // 🟢 Handle Input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewBanner((prev) => ({ ...prev, [name]: value }));
  };

  // 🟢 Handle Image Drop
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setNewBanner((prev) => ({ ...prev, imageFile: file }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewBanner((prev) => ({ ...prev, image: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  // 🟢 Add or Update Banner
  const saveBanner = async () => {
    if (!newBanner.title) {
      Toast.fire({ icon: "warning", title: "Please provide a title" });
      return;
    }
    if (!newBanner.imageFile && !editBannerUuid) {
      Toast.fire({ icon: "warning", title: "Please upload an image" });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", newBanner.title);
      formData.append("link", newBanner.link);
      formData.append("status", newBanner.status);
      if (newBanner.imageFile) formData.append("image", newBanner.imageFile);

      if (editBannerUuid) {
        await axios.put(`${baseurl}addUpdateBanner/${editBannerUuid}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Toast.fire({ icon: "success", title: "Banner updated successfully" });
      } else {
        await axios.post(`${baseurl}addUpdateBanner`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Toast.fire({ icon: "success", title: "Banner added successfully" });
      }

      // Reset & refresh
      setNewBanner({ title: "", link: "", status: "active", image: "", imageFile: null });
      setEditBannerUuid(null);
      fetchBanners();
    } catch (err) {
      console.error("Save banner error:", err);
      Toast.fire({ icon: "error", title: "Failed to save banner" });
    } finally {
      setLoading(false);
    }
  };

  // 🟢 Edit existing banner
  const editBanner = (banner) => {
    setEditBannerUuid(banner.uuid);
    setNewBanner({
      title: banner.title,
      link: banner.link,
      status: banner.status,
      image: `${imgurl}/${banner.image_path}`,
      imageFile: null,
    });
  };

  // 🟢 Delete banner
  const deleteBanner = async (uuid) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This banner will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${baseurl}deleteBannerById/${uuid}`);
      Toast.fire({ icon: "success", title: "Banner deleted successfully" });
      fetchBanners();
    } catch (err) {
      console.error("Delete banner error:", err);
      Toast.fire({ icon: "error", title: "Failed to delete banner" });
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content p-4">
          <h4 className="breadcrumb-title mb-4">Website Banner Management</h4>

          <div className="row">
            {/* Left Form */}
            <div className="col-md-4 border card p-4">
              <h5 className="mb-3">{editBannerUuid ? "Update Banner" : "Add New Banner"}</h5>

              <input
                type="text"
                className="form-control mb-3"
                placeholder="Banner Title"
                name="title"
                value={newBanner.title}
                onChange={handleChange}
              />

              <input
                type="text"
                className="form-control mb-3"
                placeholder="Redirect Link (optional)"
                name="link"
                value={newBanner.link}
                onChange={handleChange}
              />

              <div
                {...getRootProps()}
                className={`dropzone p-4 text-center border rounded mb-3 ${
                  isDragActive ? "bg-light border-primary" : "bg-white"
                }`}
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p className="text-primary fw-bold">Drop the image here...</p>
                ) : newBanner.image ? (
                  <div>
                    <img
                      src={newBanner.image}
                      alt="preview"
                      className="img-fluid rounded shadow-sm mb-2"
                      style={{ maxHeight: "150px" }}
                    />
                    <p className="text-muted small">
                      Drag & drop another image to replace
                    </p>
                  </div>
                ) : (
                  <div className="text-muted">
                    <FiImage size={30} className="mb-2" />
                    <p>Drag & drop banner image here, or click to upload</p>
                  </div>
                )}
              </div>

              <select
                className="form-select mb-3"
                name="status"
                value={newBanner.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <button className="btn btn-primary w-100" onClick={saveBanner} disabled={loading}>
                <FiUploadCloud /> {loading ? "Saving..." : editBannerUuid ? "Update Banner" : "Add Banner"}
              </button>
            </div>

            {/* Right List */}
            <div className="col-md-8">
              <div className="card p-3 border">
                <h5 className="mb-3">All Banners</h5>

                {banners.length === 0 ? (
                  <p className="text-muted">No banners added yet.</p>
                ) : (
                  <div className="row">
                    {banners.map((banner) => (
                      <div key={banner.uuid} className="col-md-6 mb-4 banner-card">
                        <div className="card shadow-sm h-100">
                          <img
                            src={`${imgurl}${banner.image_path}`}
                            alt={banner.title}
                            className="card-img-top"
                            style={{
                              height: "140px",
                              objectFit: "cover",
                              borderTopLeftRadius: "8px",
                              borderTopRightRadius: "8px",
                            }}
                          />
                          <div className="card-body">
                            <h6 className="card-title mb-1">{banner.title}</h6>
                            <p className="text-muted small mb-2">
                              {banner.link ? (
                                <a href={banner.link} target="_blank" rel="noreferrer">
                                  {banner.link}
                                </a>
                              ) : (
                                "No redirect link"
                              )}
                            </p>
                            <span
                              className={`badge ${
                                banner.status === "active" ? "bg-success" : "bg-secondary"
                              }`}
                            >
                              {banner.status}
                            </span>

                            <div className="d-flex justify-content-end gap-2 mt-2">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => editBanner(banner)}
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteBanner(banner.uuid)}
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>
        {`
          .dropzone { cursor: pointer; transition: all 0.2s; }
          .dropzone:hover { border-color: #0d6efd; background-color: #f8f9fa; }
          .banner-card img { transition: transform 0.2s ease-in-out; }
          .banner-card:hover img { transform: scale(1.05); }
        `}
      </style>
    </>
  );
}

export default InterfaceControl;
